import { API_BASE_URL } from "../config/api";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useSearchParams } from "react-router-dom";
import {
  CONTACT_ROLE_OPTIONS,
  cloneAddress,
  createEmptyCustomerForm,
  createEmptyContact,
  ensurePhonePrefix,
  sanitizeCustomerPayload
} from "../utils/customerForm";
import { getContactNumberWarning, getGstinWarning } from "../utils/formValidation";
import { fetchLocationByPincode, normalizePincode } from "../utils/locationApi";

const inputClassName =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100";
const warningClassName = "mt-1 text-xs text-red-500";

export default function ClientOnboarding() {
  const { token: routeToken } = useParams();
  const [searchParams] = useSearchParams();
  const token = routeToken || searchParams.get("token") || "";

  const [validationState, setValidationState] = useState({
    loading: true,
    valid: false,
    isSuccess: false,
    customerName: "",
    submissionDate: null,
    message: ""
  });

  const [form, setForm] = useState(createEmptyCustomerForm);
  const [submitting, setSubmitting] = useState(false);
  const [useBillingForWorkAddress, setUseBillingForWorkAddress] = useState(false);
  const [locationStatus, setLocationStatus] = useState({
    billingAddress: { loading: false, message: "", tone: "idle" },
    address: { loading: false, message: "", tone: "idle" }
  });

  const phoneWarning = getContactNumberWarning(form.phone);
  const gstWarning = getGstinWarning(form.gstNumber);
  const isSezSupply = String(form.gstType || "").trim().toLowerCase() === "sez_supply";

  useEffect(() => {
    if (!token) {
      setValidationState({
        loading: false,
        valid: false,
        message: "This onboarding link is invalid or has expired."
      });
      return;
    }

    const validateToken = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/customers/onboarding/validate/${token}`);
        if (response.data?.valid) {
          setValidationState({
            loading: false,
            valid: true,
            message: ""
          });
        }
      } catch (error) {
        setValidationState({
          loading: false,
          valid: false,
          message: error.response?.data?.message || "This onboarding link is invalid or has expired."
        });
      }
    };

    validateToken();
  }, [token]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({
      ...current,
      [name]: name === "phone"
        ? ensurePhonePrefix(value)
        : value,
      ...(name === "gstType" && value !== "sez_supply" ? { lutRefNo: "" } : {})
    }));
  };

  const handleAddressChange = (section, event) => {
    const { name, value } = event.target;
    const nextValue = name === "pincode" ? normalizePincode(value) : value;

    setForm((current) => {
      const nextSection = {
        ...current[section],
        [name]: nextValue
      };

      return {
        ...current,
        [section]: nextSection,
        ...(section === "billingAddress" && useBillingForWorkAddress
          ? { address: cloneAddress(nextSection) }
          : {})
      };
    });

    if (name === "pincode") {
      setLocationStatus((current) => ({
        ...current,
        [section]:
          nextValue.length === 6
            ? current[section]
            : { loading: false, message: "", tone: "idle" }
      }));
    }
  };

  const handlePincodeBlur = async (section) => {
    const pincode = normalizePincode(form[section]?.pincode);

    if (!pincode) {
      setLocationStatus((current) => ({
        ...current,
        [section]: { loading: false, message: "", tone: "idle" }
      }));
      return;
    }

    if (pincode.length !== 6) {
      setLocationStatus((current) => ({
        ...current,
        [section]: { loading: false, message: "Enter a valid 6-digit pincode.", tone: "error" }
      }));
      return;
    }

    setLocationStatus((current) => ({
      ...current,
      [section]: { loading: true, message: "Looking up city and state...", tone: "loading" }
    }));

    try {
      const location = await fetchLocationByPincode(pincode);

      setForm((current) => {
        const nextSection = {
          ...current[section],
          city: location.city || current[section].city,
          state: location.state || current[section].state
        };

        return {
          ...current,
          [section]: nextSection,
          ...(section === "billingAddress" && useBillingForWorkAddress
            ? { address: cloneAddress(nextSection) }
            : {})
        };
      });

      setLocationStatus((current) => ({
        ...current,
        [section]: {
          loading: false,
          message: "City and state updated from pincode.",
          tone: "success"
        }
      }));
    } catch (error) {
      setLocationStatus((current) => ({
        ...current,
        [section]: {
          loading: false,
          message: error.response?.data?.message || error.message || "Unable to fetch location.",
          tone: "error"
        }
      }));
    }
  };

  const getLocationMessageClassName = (tone) => {
    if (tone === "error") return warningClassName;
    if (tone === "success") return "mt-1 text-xs text-emerald-600";
    return "mt-1 text-xs text-slate-500";
  };

  const handleAdditionalChange = (index, field, value) => {
    setForm((current) => ({
      ...current,
      additionalContacts: current.additionalContacts.map((contact, contactIndex) => {
        if (contactIndex !== index) {
          return field === "isPrimary"
            ? { ...contact, isPrimary: false }
            : contact;
        }

        return {
          ...contact,
          [field]:
            field === "mobileNumber" || field === "whatsappNumber"
              ? ensurePhonePrefix(value)
              : field === "isPrimary"
                ? Boolean(value)
                : value,
          isPrimary: field === "isPrimary" ? Boolean(value) : contact.isPrimary
        };
      })
    }));
  };

  const addContact = () => {
    setForm((current) => ({
      ...current,
      additionalContacts: [
        ...current.additionalContacts,
        createEmptyContact(!current.additionalContacts.some((contact) => contact.isPrimary))
      ]
    }));
  };

  const removeContact = (index) => {
    setForm((current) => ({
      ...current,
      additionalContacts:
        current.additionalContacts.length === 1
          ? [createEmptyContact(true)]
          : current.additionalContacts
              .filter((_, contactIndex) => contactIndex !== index)
              .map((contact, contactIndex) => ({
                ...contact,
                isPrimary: contactIndex === 0
              }))
    }));
  };

  const handleUseBillingForWorkAddressChange = (event) => {
    const checked = event.target.checked;
    setUseBillingForWorkAddress(checked);

    if (checked) {
      setForm((current) => ({
        ...current,
        address: cloneAddress(current.billingAddress)
      }));

      setLocationStatus((current) => ({
        ...current,
        address: { ...current.billingAddress }
      }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      setSubmitting(true);
      const response = await axios.post(`${API_BASE_URL}/api/customers/onboarding/submit/${token}`, sanitizeCustomerPayload(form));
      setValidationState({
        loading: false,
        valid: false,
        isSuccess: true,
        customerName: response.data?.customerName,
        submissionDate: response.data?.submissionDate,
        message: "Thank you. Your customer profile has been submitted successfully."
      });
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.message || "Error submitting profile information. Please verify the fields and retry.");
    } finally {
      setSubmitting(false);
    }
  };

  if (validationState.loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="text-center text-slate-500 font-semibold">
          Verifying registration token...
        </div>
      </div>
    );
  }

  if (validationState.isSuccess) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
        <div className="w-full max-w-lg rounded-[28px] border border-slate-200 bg-white p-8 shadow-sm text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
            <svg xmlns="http://www.w3.org/2005/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-7 w-7">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 0 1-1.043 3.296 3.745 3.745 0 0 1-3.296 1.043A3.745 3.745 0 0 1 12 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 0 1-3.296-1.043 3.745 3.745 0 0 1-1.043-3.296A3.745 3.745 0 0 1 3 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 0 1 1.043-3.296 3.746 3.746 0 0 1 3.296-1.043A3.746 3.746 0 0 1 12 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 0 1 3.296 1.043 3.746 3.746 0 0 1 1.043 3.296A3.745 3.745 0 0 1 21 12Z" />
            </svg>
          </div>
          <h2 className="mt-6 text-xl font-bold text-slate-900">Success!</h2>
          <p className="mt-4 text-sm leading-6 text-slate-600">
            {validationState.message}
          </p>
          {validationState.customerName && (
            <div className="mt-6 border-t border-slate-100 pt-4 text-left">
              <div className="flex justify-between text-sm py-1.5">
                <span className="text-slate-500 font-medium">Customer Name:</span>
                <span className="text-slate-800 font-semibold">{validationState.customerName}</span>
              </div>
              {validationState.submissionDate && (
                <div className="flex justify-between text-sm py-1.5">
                  <span className="text-slate-500 font-medium">Submission Date:</span>
                  <span className="text-slate-800 font-semibold">
                    {new Date(validationState.submissionDate).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit"
                    })}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!validationState.valid) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
        <div className="w-full max-w-lg rounded-[28px] border border-slate-200 bg-white p-8 shadow-sm text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-blue-50 text-blue-600">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-7 w-7">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 0 1-1.043 3.296 3.745 3.745 0 0 1-3.296 1.043A3.745 3.745 0 0 1 12 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 0 1-3.296-1.043 3.745 3.745 0 0 1-1.043-3.296A3.745 3.745 0 0 1 3 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 0 1 1.043-3.296 3.746 3.746 0 0 1 3.296-1.043A3.746 3.746 0 0 1 12 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 0 1 3.296 1.043 3.746 3.746 0 0 1 1.043 3.296A3.745 3.745 0 0 1 21 12Z" />
            </svg>
          </div>
          <h2 className="mt-6 text-xl font-bold text-slate-900">Registration Status</h2>
          <p className="mt-4 text-sm leading-6 text-slate-600">
            {validationState.message}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8 sm:px-6">
      <div className="mx-auto max-w-4xl">
        <section className="rounded-[28px] border border-slate-200 bg-[linear-gradient(135deg,_#ffffff,_#f8fafc,_#f0f7ff)] p-6 shadow-sm">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-blue-700">
            Client Onboarding
          </p>
          <h1 className="mt-2 text-3xl font-bold text-slate-900">Business Registration Profile</h1>
        </section>

        <form onSubmit={handleSubmit} className="mt-6 space-y-6">
          {/* Card 1: Primary Details */}
          <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-bold text-slate-900 mb-5">🏢 PRIMARY BUSINESS DETAILS</h2>
            <div className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">Registered Company Name</label>
                <input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  className={inputClassName}
                  placeholder="Enter registered company name"
                  required
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">Primary Coordinator / Authorized Signatory</label>
                <input
                  name="contactPerson"
                  value={form.contactPerson}
                  onChange={handleChange}
                  className={inputClassName}
                  placeholder="Name of primary contact person"
                  required
                />
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-semibold text-slate-700">Phone / Telephone</label>
                  <input
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    className={inputClassName}
                    placeholder="Mobile, landline, or telephone number"
                    required
                  />
                  {phoneWarning ? <p className={warningClassName}>{phoneWarning}</p> : null}
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-slate-700">Official Email Address</label>
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    className={inputClassName}
                    placeholder="Email address for notifications"
                    required
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-slate-700">GST Type</label>
                  <select
                    name="gstType"
                    value={form.gstType}
                    onChange={handleChange}
                    className={inputClassName}
                  >
                    <option value="cgst_sgst">GST</option>
                    <option value="igst">IGST</option>
                    <option value="sez_supply">SEZ SUPPLY</option>
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-slate-700">GST No</label>
                  <input
                    name="gstNumber"
                    value={form.gstNumber}
                    onChange={handleChange}
                    className={inputClassName}
                    placeholder="15-digit GST number"
                  />
                  {gstWarning ? <p className={warningClassName}>{gstWarning}</p> : null}
                </div>

                {isSezSupply ? (
                  <div className="md:col-span-2">
                    <label className="mb-2 block text-sm font-semibold text-slate-700">LUT Ref No</label>
                    <input
                      name="lutRefNo"
                      value={form.lutRefNo}
                      onChange={handleChange}
                      className={inputClassName}
                      placeholder="Enter LUT reference number"
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </section>

          {/* Card 2: Billing Address */}
          <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-bold text-slate-900 mb-5">📍 BILLING ADDRESS</h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-semibold text-slate-700">Street</label>
                <input
                  name="street"
                  value={form.billingAddress.street}
                  onChange={(event) => handleAddressChange("billingAddress", event)}
                  className={inputClassName}
                  placeholder="Billing street address"
                  required
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">Pincode</label>
                <input
                  name="pincode"
                  value={form.billingAddress.pincode}
                  onChange={(event) => handleAddressChange("billingAddress", event)}
                  onBlur={() => handlePincodeBlur("billingAddress")}
                  className={inputClassName}
                  placeholder="Pincode"
                  required
                />
                {locationStatus.billingAddress.message ? (
                  <p className={getLocationMessageClassName(locationStatus.billingAddress.tone)}>
                    {locationStatus.billingAddress.message}
                  </p>
                ) : null}
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">City</label>
                <input
                  name="city"
                  value={form.billingAddress.city}
                  onChange={(event) => handleAddressChange("billingAddress", event)}
                  className={inputClassName}
                  placeholder="City"
                  required
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">State</label>
                <input
                  name="state"
                  value={form.billingAddress.state}
                  onChange={(event) => handleAddressChange("billingAddress", event)}
                  className={inputClassName}
                  placeholder="State"
                  required
                />
              </div>
            </div>

            <label className="mt-6 flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-100/80 transition cursor-pointer">
              <input
                type="checkbox"
                checked={useBillingForWorkAddress}
                onChange={handleUseBillingForWorkAddressChange}
                className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              />
              Use the same address for work / delivery address
            </label>
          </section>

          {/* Card 3: Work / Delivery Address */}
          <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-bold text-slate-900 mb-5">🚚 WORK / DELIVERY ADDRESS</h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-semibold text-slate-700">Street</label>
                <input
                  name="street"
                  value={form.address.street}
                  onChange={(event) => handleAddressChange("address", event)}
                  disabled={useBillingForWorkAddress}
                  className={inputClassName}
                  placeholder="Delivery street address"
                  required={!useBillingForWorkAddress}
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">Pincode</label>
                <input
                  name="pincode"
                  value={form.address.pincode}
                  onChange={(event) => handleAddressChange("address", event)}
                  onBlur={() => handlePincodeBlur("address")}
                  disabled={useBillingForWorkAddress}
                  className={inputClassName}
                  placeholder="Pincode"
                  required={!useBillingForWorkAddress}
                />
                {locationStatus.address.message ? (
                  <p className={getLocationMessageClassName(locationStatus.address.tone)}>
                    {locationStatus.address.message}
                  </p>
                ) : null}
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">City</label>
                <input
                  name="city"
                  value={form.address.city}
                  onChange={(event) => handleAddressChange("address", event)}
                  disabled={useBillingForWorkAddress}
                  className={inputClassName}
                  placeholder="City"
                  required={!useBillingForWorkAddress}
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">State</label>
                <input
                  name="state"
                  value={form.address.state}
                  onChange={(event) => handleAddressChange("address", event)}
                  disabled={useBillingForWorkAddress}
                  className={inputClassName}
                  placeholder="State"
                  required={!useBillingForWorkAddress}
                />
              </div>
            </div>
          </section>

          {/* Card 4: Additional Contacts */}
          <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between gap-4 mb-5 border-b border-slate-100 pb-4">
              <h2 className="text-lg font-bold text-slate-900">👥 ADDITIONAL POINT OF CONTACTS</h2>
              <button
                type="button"
                onClick={addContact}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
              >
                + Add Contact
              </button>
            </div>

            <div className="space-y-6">
              {form.additionalContacts.map((contact, index) => (
                <div
                  key={`contact-${index}`}
                  className="rounded-3xl border border-slate-200 bg-slate-50/50 p-5 hover:bg-slate-50 transition"
                >
                  <div className="flex flex-col gap-5 lg:flex-row lg:items-start">
                    <div className="grid flex-1 grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Role</label>
                        <select
                          value={contact.role}
                          onChange={(event) => handleAdditionalChange(index, "role", event.target.value)}
                          className={inputClassName}
                        >
                          {CONTACT_ROLE_OPTIONS.map((roleOption) => (
                            <option key={roleOption} value={roleOption}>
                              {roleOption}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Contact Person Name</label>
                        <input
                          placeholder="Enter contact name"
                          value={contact.name}
                          onChange={(event) => handleAdditionalChange(index, "name", event.target.value)}
                          className={inputClassName}
                          required
                        />
                      </div>

                      <div>
                        <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Mobile Number</label>
                        <input
                          placeholder="Mobile number"
                          value={contact.mobileNumber}
                          onChange={(event) => handleAdditionalChange(index, "mobileNumber", event.target.value)}
                          className={inputClassName}
                          required
                        />
                      </div>

                      <div>
                        <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Email Address</label>
                        <input
                          type="email"
                          placeholder="Email address"
                          value={contact.email}
                          onChange={(event) => handleAdditionalChange(index, "email", event.target.value)}
                          className={inputClassName}
                        />
                      </div>

                      <div>
                        <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">WhatsApp Number</label>
                        <input
                          placeholder="WhatsApp number"
                          value={contact.whatsappNumber}
                          onChange={(event) => handleAdditionalChange(index, "whatsappNumber", event.target.value)}
                          className={inputClassName}
                        />
                      </div>

                      <div>
                        <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Remarks</label>
                        <input
                          placeholder="Remarks"
                          value={contact.remarks}
                          onChange={(event) => handleAdditionalChange(index, "remarks", event.target.value)}
                          className={inputClassName}
                        />
                      </div>
                    </div>

                    <div className="flex shrink-0 flex-row gap-3 lg:flex-col lg:w-44">
                      <label className="flex flex-1 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 cursor-pointer hover:bg-slate-50 select-none">
                        <input
                          type="radio"
                          name="primary-contact"
                          checked={Boolean(contact.isPrimary)}
                          onChange={() =>
                            setForm((current) => ({
                              ...current,
                              additionalContacts: current.additionalContacts.map((item, itemIndex) => ({
                                ...item,
                                isPrimary: itemIndex === index
                              }))
                            }))
                          }
                          className="h-4 w-4 border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                        Primary
                      </label>
                      <button
                        type="button"
                        onClick={() => removeContact(index)}
                        className="flex-1 rounded-2xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-600 transition hover:bg-red-100/80"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Card 5: Additional Info */}
          <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-bold text-slate-900 mb-5">Additional Info</h2>
            <textarea
              name="additionalInfo"
              value={form.additionalInfo}
              onChange={handleChange}
              className={`${inputClassName} min-h-32 resize-y`}
              placeholder="Any other details you want our team to know"
            />
          </section>

          {/* Action Row */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={submitting}
              className={`rounded-2xl px-6 py-3.5 text-sm font-semibold text-white transition shadow-sm ${
                submitting ? "bg-slate-400" : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {submitting ? "Submitting Registration..." : "Submit Profile & Complete Onboarding"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
